import './App.css'
import * as React from 'react'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider,
  ListItem, ListItemButton, ListItemIcon, ListItemText, CssBaseline,
  Avatar, Chip, Collapse, IconButton, Menu, MenuItem, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  Select, FormControl, InputLabel, Alert, CircularProgress
} from '@mui/material'
import CastleIcon from '@mui/icons-material/Castle'
import DashboardIcon from '@mui/icons-material/Dashboard'
import SettingsIcon from '@mui/icons-material/Settings'
import LogoutIcon from '@mui/icons-material/Logout'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ExtensionIcon from '@mui/icons-material/Extension'
import LanguageIcon from '@mui/icons-material/Language'
import GpsFixedIcon from '@mui/icons-material/GpsFixed'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'

import GGEUserTable from './modules/GGEUsersTable'
import UserSettings from './modules/userSettings'
import { ErrorType, ActionType, User } from "./types.js"
import { getTranslation } from './translations.js'
import ReconnectingWebSocket from "reconnecting-websocket"

const drawerWidth = 380;

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#90caf9' },
    background: { default: '#050c1a', paper: '#0a1929' }
  },
  typography: { fontFamily: 'Inter, sans-serif' }
})

function App() {
  const [activeView, setActiveView] = React.useState('dashboard');
  const [settingsTab, setSettingsTab] = React.useState('account');
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [attackGroupOpen, setAttackGroupOpen] = React.useState(false);
  const [productionGroupOpen, setProductionGroupOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [users, setUsers] = React.useState([])
  const [usersStatus, setUsersStatus] = React.useState({})
  const [plugins, setPlugins] = React.useState([])
  const [isAdmin, setIsAdmin] = React.useState(false)
  const [announcement, setAnnouncement] = React.useState(null)
  const [dismissedAnnouncement, setDismissedAnnouncement] = React.useState(() => {
    try { return localStorage.getItem('dismissedAnnouncement') } catch { return null }
  })
  const dismissAnnouncement = () => {
    setDismissedAnnouncement(announcement?.id)
    try { localStorage.setItem('dismissedAnnouncement', announcement?.id) } catch { }
  }
  const [allAccounts, setAllAccounts] = React.useState([])
  const [adminViewingUser, setAdminViewingUser] = React.useState(null)
  const [linkedAccounts, setLinkedAccounts] = React.useState([])
  const [linkedViewingUser, setLinkedViewingUser] = React.useState(null)
const [language, setLanguage] = React.useState(localStorage.getItem('lang') || 'tr');
  const [anchorEl, setAnchorEl] = React.useState(null);

  // Credential Verification States
  const [credentialError, setCredentialError] = React.useState('');
  const [credentialVerifying, setCredentialVerifying] = React.useState(false);

  // Castle Setup Modal States
  const [showCastleModal, setShowCastleModal] = React.useState(false);
  const [castleStatus, setCastleStatus] = React.useState(null);
  const [castleForm, setCastleForm] = React.useState({ server: '', username: '', password: '' });
  const [castleError, setCastleError] = React.useState('');
  const [castleLoading, setCastleLoading] = React.useState(false);
  const [instances, setInstances] = React.useState([]);
  const [langData, setLangData] = React.useState({});
  const selectedUserRef = React.useRef(null);
  React.useEffect(() => { selectedUserRef.current = selectedUser; }, [selectedUser]);
  const adminViewingUserRef = React.useRef(null);
  React.useEffect(() => { adminViewingUserRef.current = adminViewingUser; }, [adminViewingUser]);
  const pendingPluginStatesRef = React.useRef(new Map());
  const pluginRequestIdRef = React.useRef(0);
  const linkedViewingUserRef = React.useRef(null);
  React.useEffect(() => { linkedViewingUserRef.current = linkedViewingUser; }, [linkedViewingUser]);

  const t = (key) => getTranslation(language, key);

  let ws = React.useMemo(() => {
    const ws = new ReconnectingWebSocket(`${window.location.protocol === 'https:' ? "wss" : "ws"}://${window.location.hostname}:${window.location.port}`, [], { WebSocket: WebSocket, minReconnectionDelay: 3000 })
    ws.onmessage = (msg) => {
      let [err, action, obj] = JSON.parse(msg.data.toString())
      if (action === ActionType.GetUsers && err === ErrorType.Success) {
        console.log("Loaded Plugins:", obj[1]);
        const uList = obj[0].map(e => {
          const user = new User(e);
          pendingPluginStatesRef.current.forEach((pending, key) => {
            const [accountId, pluginKey] = key.split(':');
            if (Number(accountId) !== Number(user.id)) return;
            user.plugins = { ...(user.plugins || {}) };
            user.plugins[pluginKey] = {
              ...(user.plugins[pluginKey] || {}),
              state: pending.enabled
            };
          });
          return user;
        });
        setUsers(uList); setPlugins(obj[1]);
        if (uList.length > 0 && !selectedUserRef.current) {
          setSelectedUser(uList[0]);
        } else if (selectedUserRef.current) {
          const fresh = uList.find(u => u.id === selectedUserRef.current.id);
          if (fresh) setSelectedUser(prev => ({ ...fresh, plugins: { ...fresh.plugins } }));
        }
        // Kayıt başarılı - doğrulama durumunu temizle
        setCredentialVerifying(false);
        setCredentialError('');
        // Admin bayrağını güncelle
        if (obj[2] !== undefined) setIsAdmin(!!obj[2]);
        if (Array.isArray(obj[3])) setLinkedAccounts(obj[3]);
        if (obj[4] !== undefined && obj[2] !== true) {
          const viewedUserId = Number(obj[4]);
          setLinkedViewingUser(viewedUserId);
          linkedViewingUserRef.current = viewedUserId;
        }
        if (obj[2] === true) {
          ws.send(JSON.stringify([ErrorType.Success, ActionType.AdminGetAllAccounts, {}]));
        }
      } else if (action === ActionType.Announcement && err === ErrorType.Success) {
        setAnnouncement(obj && obj.text ? obj : null);
      } else if (action === ActionType.AdminGetAllAccounts && err === ErrorType.Success) {
        setAllAccounts(obj);
      } else if (action === ActionType.SelectLinkedAccount && err !== ErrorType.Success) {
        window.alert(obj?.error || 'Hesap değiştirilemedi.');
      } else if (action === ActionType.SetUser) {
        if (err !== ErrorType.Success) {
          // Doğrulama başarısız
          setCredentialVerifying(false);
          setCredentialError(obj?.error || 'Doğrulama başarısız');
        } else if (obj?.verifying) {
          // Doğrulama devam ediyor
          setCredentialVerifying(true);
          setCredentialError('');
        }
      } else if (action === ActionType.SetPluginState) {
        const accountId = Number(obj?.accountId);
        const pluginKey = String(obj?.pluginKey || '');
        const pendingKey = `${accountId}:${pluginKey}`;
        const pending = pendingPluginStatesRef.current.get(pendingKey);
        if (pending && pending.requestId === obj?.requestId) {
          pendingPluginStatesRef.current.delete(pendingKey);
          if (err !== ErrorType.Success) {
            window.alert(obj?.error || 'Eklenti durumu değiştirilemedi.');
            ws.send(JSON.stringify([ErrorType.Success, ActionType.GetUsers, {}]));
          }
        }
      } else if (action === ActionType.StatusUser) {
        console.debug("Live Status Received:", obj);
        setUsersStatus(prev => ({ ...prev, [obj.id]: obj }));
      } else if (action === ActionType.GetUUID && err === ErrorType.Unauthenticated) {
        window.location.href = "signin.html";
      } else if (action === ActionType.GetCastleStatus) {
        setCastleStatus(obj);
        // Kale yoksa veya kilitli değilse modal göster
        if (!obj.hasCastle || !obj.isLocked) {
          setShowCastleModal(true);
        }
      } else if (action === ActionType.VerifyCastle) {
        setCastleLoading(false);
        if (err === ErrorType.Success && obj.success) {
          setShowCastleModal(false);
          setCastleError('');
          // Kullanıcı listesini yenile
          ws.send(JSON.stringify([ErrorType.Success, ActionType.GetUsers, {}]));
        } else {
          setCastleError(obj.error || 'Bir hata oluştu');
        }
      }
    }
    // Bağlantı açıldığında kale durumunu kontrol et
    ws.onopen = () => {
      pendingPluginStatesRef.current.clear();
      ws.send(JSON.stringify([ErrorType.Success, ActionType.GetCastleStatus, {}]));
      // Yeniden bağlanırken admin seçimi sunucu tarafında kaybolur, geri yükle
      if (adminViewingUserRef.current) {
        ws.send(JSON.stringify([ErrorType.Success, ActionType.AdminSelectAccount, { userId: adminViewingUserRef.current }]));
      } else if (linkedViewingUserRef.current) {
        ws.send(JSON.stringify([ErrorType.Success, ActionType.SelectLinkedAccount, { userId: linkedViewingUserRef.current }]));
      }
    }
    return ws
  }, []) // Bağlantı yalnızca bir kez kurulur; selectedUser değiştiğinde yeni WS açılmaz

  // Sunucu listesini yükle
  React.useEffect(() => {
    const fetchInstances = async () => {
      try {
        const protocol = window.location.protocol === 'https:' ? "https" : "http";
        const host = `${protocol}://${window.location.hostname}:${window.location.port}`;
        const langRes = await fetch(`${host}/lang.json`);
        setLangData(await langRes.json());
        const xmlRes = await fetch(`${host}/1.xml`);
        const xmlDoc = new DOMParser().parseFromString(await xmlRes.text(), "text/xml");
        const _instances = xmlDoc.getElementsByTagName("instance");
        const loadedInstances = [];
        for (let i = 0; i < _instances.length; i++) {
          const obj = _instances[i];
          let sName, locaId, iName;
          for (let j = 0; j < obj.childNodes.length; j++) {
            const c = obj.childNodes[j];
            if (c.nodeName === "server") sName = c.textContent;
            if (c.nodeName === "instanceLocaId") locaId = c.textContent;
            if (c.nodeName === "instanceName") iName = c.textContent;
          }
          if (locaId) loadedInstances.push({ id: obj.getAttribute("value"), server: sName, instanceLocaId: locaId, instanceName: iName });
        }
        setInstances(loadedInstances);
        if (loadedInstances.length > 0 && !castleForm.server) {
          setCastleForm(prev => ({ ...prev, server: loadedInstances[0].id }));
        }
      } catch (error) { console.error(error); }
    };
    fetchInstances();
  }, []);

  const handleCastleSubmit = () => {
    if (!castleForm.server || !castleForm.username || !castleForm.password) {
      setCastleError('Tüm alanları doldurun');
      return;
    }
    setCastleLoading(true);
    setCastleError('');
    ws.send(JSON.stringify([ErrorType.Success, ActionType.VerifyCastle, castleForm]));
  };

  const handleLangMenu = (event) => setAnchorEl(event.currentTarget);
  const handleLangSelect = (lang) => { setLanguage(lang); localStorage.setItem('lang', lang); setAnchorEl(null); };

  const handleBotSelect = (user) => {
    setSelectedUser(user); setActiveView('settings'); setSettingsOpen(true); setSettingsTab('account');
  };

  const handleAdminSelectUser = (account) => {
    const targetUserId = account?.userId || null;
    setAdminViewingUser(targetUserId);
    adminViewingUserRef.current = targetUserId;
    setSelectedUser(null);
    selectedUserRef.current = null;
    ws.send(JSON.stringify([ErrorType.Success, ActionType.AdminSelectAccount, { userId: targetUserId }]));
  };

  const handleLinkedSelectUser = (account) => {
    const targetUserId = Number(account?.userId);
    if (!Number.isInteger(targetUserId)) return;
    setLinkedViewingUser(targetUserId);
    linkedViewingUserRef.current = targetUserId;
    setSelectedUser(null);
    selectedUserRef.current = null;
    setActiveView('dashboard');
    ws.send(JSON.stringify([ErrorType.Success, ActionType.SelectLinkedAccount, { userId: targetUserId }]));
  };

  const setPluginState = (pluginKey, enabled) => {
    const currentUser = selectedUserRef.current;
    if (!currentUser?.id) return;

    const updatedUser = { ...currentUser };
    updatedUser.plugins = JSON.parse(JSON.stringify(updatedUser.plugins || {}));
    if (!updatedUser.plugins[pluginKey]) updatedUser.plugins[pluginKey] = {};
    updatedUser.plugins[pluginKey].state = !!enabled;

    const requestId = `${Date.now()}-${++pluginRequestIdRef.current}`;
    pendingPluginStatesRef.current.set(`${updatedUser.id}:${pluginKey}`, {
      requestId,
      enabled: !!enabled
    });

    selectedUserRef.current = updatedUser;
    setSelectedUser(updatedUser);
    setUsers(prevUsers => prevUsers.map(u => u.id === updatedUser.id ? updatedUser : u));
    ws.send(JSON.stringify([ErrorType.Success, ActionType.SetPluginState, {
      accountId: updatedUser.id,
      pluginKey,
      enabled: !!enabled,
      requestId
    }]));
  };

  const togglePluginFromSidebar = (e, pluginKey) => {
    e.stopPropagation();
    const currentState = !!selectedUserRef.current?.plugins?.[pluginKey]?.state;
    setPluginState(pluginKey, !currentState);
  };

  // Sidebar'daki eklenti listesi iki katlanır gruba ayrılıyor: dosyası plugins/attack/ altında
  // olanlar "Saldırı Eklentileri", geri kalanı "Üretim / Alet / Asker". "attack" (SALDIRI
  // AYARLARI ana sekmesi) ve "presets" (yardımcı, UI'da gizli) hiçbir gruba dahil değil.
  const nonTabPlugins = plugins.filter(p => p.key !== 'presets' && p.key !== 'attack');
  const attackGroupPlugins = nonTabPlugins.filter(p => p.filename?.startsWith('plugins/attack/'));
  const productionGroupPlugins = nonTabPlugins.filter(p => !p.filename?.startsWith('plugins/attack/'));
  // Bakımdaki eklenti kullanıcı açık bıraksa bile çalışmaz — aktif sayılmaz, kapalı görünür.
  const isPluginOn = (plugin) => !plugin.maintenance && !!selectedUser?.plugins?.[plugin.key]?.state;
  const countActive = (list) => list.filter(isPluginOn).length;
  const PLUGIN_ICON_OVERRIDES = {
    attackbarronsempire: '/plugin-icons/baron-tower.png',
    attackbarronsfire: '/plugin-icons/baron-fire.png',
    attackbarronsglacier: '/plugin-icons/baron-glacier.png',
    attackbarronssands: '/plugin-icons/baron-sands.png',
    attackberi: '/plugin-icons/beri-camp.png',
    attackbloodcrows: '/plugin-icons/bloodcrows-foreign.png',
    attackforeigncastles: '/plugin-icons/bloodcrows-foreign.png',
    attackfortresses: '/plugin-icons/fortress-fire.png',
    attackfortresses_sand: '/plugin-icons/fortress-sand.png',
    attackfortresses_glacier: '/plugin-icons/fortress-glacier.png',
    attackforts: '/plugin-icons/aqua-forts.png',
    attackkhan: '/plugin-icons/khan.png',
    attacknomads: '/plugin-icons/nomad.png',
    gocebeDanisman: '/plugin-icons/nomad.png',
    attacksams: '/plugin-icons/samurai.png',
    samurayDanisman: '/plugin-icons/samurai.png',
    berikingdom: '/plugin-icons/berikingdom.png',
    allianceaid: '/plugin-icons/allianceaid.png',
    autoEkipmanSat: '/plugin-icons/autoekipmansat.png',
    autoRecruit: '/plugin-icons/autorecruit.png',
    aletBasma: '/plugin-icons/aletbasma.png',
    toolbuyer: '/plugin-icons/toolbuyer.png',
    support: '/plugin-icons/support.png',
    rafineri: '/plugin-icons/rafineri.png',
    meadrep: '/plugin-icons/meadrep.png',
  };
  const renderPluginItem = (plugin) => {
    const iconOverride = PLUGIN_ICON_OVERRIDES[plugin.key];
    return (
      <ListItemButton
        key={plugin.key}
        selected={activeView === 'settings' && settingsTab === plugin.key}
        onClick={() => { setActiveView('settings'); setSettingsTab(plugin.key); }}
        sx={{ borderRadius: '10px 0 0 10px', py: 1, borderLeft: isPluginOn(plugin) ? '3px solid #4caf50' : '3px solid transparent' }}
      >
        <Checkbox
          size="small"
          checked={isPluginOn(plugin)}
          disabled={!!plugin.maintenance}
          onClick={(e) => togglePluginFromSidebar(e, plugin.key)}
          sx={{ p: 0.5, mr: 1, color: '#666', '&.Mui-checked': { color: '#4caf50' } }}
        />
        <ListItemIcon sx={{ minWidth: 42, opacity: plugin.maintenance ? 0.45 : 1 }}>
          {iconOverride
            ? <Box component="img" src={iconOverride} alt="" sx={{ width: 34, height: 34, objectFit: 'contain', borderRadius: 1 }} />
            : <ExtensionIcon sx={{ fontSize: 29, color: isPluginOn(plugin) ? '#4caf50' : 'inherit' }} />}
        </ListItemIcon>
        <ListItemText primary={t(plugin.name)} primaryTypographyProps={{ fontSize: '0.92rem', sx: { opacity: plugin.maintenance ? 0.6 : 1 } }} />
        {plugin.maintenance && (
          <Chip label="🛠 Bakımda" size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(255,167,38,0.15)', color: '#ffa726' }} />
        )}
      </ListItemButton>
    );
  };
  const renderPluginGroup = (label, icon, iconColor, list, open, setOpen, imgSrc) => (
    <>
      <ListItemButton onClick={() => setOpen(!open)} sx={{ borderRadius: '10px 0 0 10px', mt: 1, py: 1.2 }}>
        <ListItemIcon sx={{ minWidth: 42 }}>
          {imgSrc
            ? <Box component="img" src={imgSrc} alt="" sx={{ width: 31, height: 31, objectFit: 'contain' }} />
            : React.cloneElement(icon, { sx: { fontSize: 31, color: iconColor } })}
        </ListItemIcon>
        <ListItemText primary={t(label)} primaryTypographyProps={{ fontSize: '1rem', fontWeight: 'bold' }} />
        {countActive(list) > 0 && (
          <Chip label={countActive(list)} size="small" sx={{ height: 22, fontSize: '0.75rem', bgcolor: 'rgba(76,175,80,0.15)', color: '#4caf50', mr: 0.5 }} />
        )}
        {open ? <ExpandLess sx={{ fontSize: 22 }} /> : <ExpandMore sx={{ fontSize: 22 }} />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: 1.5 }}>
          {list.map(renderPluginItem)}
        </List>
      </Collapse>
    </>
  );

  // Seçili sekme bir gruba aitse, sayfa yüklenince/sekme değişince o grup otomatik açılsın —
  // kullanıcı az önce ayarladığı eklentiyi kaybetmesin.
  React.useEffect(() => {
    if (attackGroupPlugins.some(p => p.key === settingsTab)) setAttackGroupOpen(true);
    if (productionGroupPlugins.some(p => p.key === settingsTab)) setProductionGroupOpen(true);
  }, [settingsTab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ThemeProvider theme={darkTheme}>
      {/* Kale Kurulum Modal */}
      <Dialog
        open={showCastleModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#0a1929', border: '1px solid rgba(144, 202, 249, 0.3)' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <CastleIcon sx={{ color: '#90caf9' }} />
          <Typography variant="h6">{t("Kale Bağlantısı")}</Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t("Bu işlem tek seferlik! Kale atandıktan sonra değiştirilemez.")}
          </Alert>

          {castleError && (
            <Alert severity="error" sx={{ mb: 2 }}>{castleError}</Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("Sunucu")}</InputLabel>
            <Select
              value={castleForm.server}
              onChange={(e) => setCastleForm(prev => ({ ...prev, server: e.target.value }))}
              label={t("Sunucu")}
            >
              {instances.map((inst, i) => (
                <MenuItem value={inst.id} key={i}>
                  {(langData[inst.instanceLocaId] || inst.instanceLocaId) + ' ' + inst.instanceName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t("Oyun Kullanıcı Adı")}
            value={castleForm.username}
            onChange={(e) => setCastleForm(prev => ({ ...prev, username: e.target.value }))}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label={t("Oyun Şifresi")}
            type="password"
            value={castleForm.password}
            onChange={(e) => setCastleForm(prev => ({ ...prev, password: e.target.value }))}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button
            variant="contained"
            onClick={handleCastleSubmit}
            disabled={castleLoading}
            startIcon={castleLoading ? <CircularProgress size={20} /> : <CastleIcon />}
            sx={{ px: 4 }}
          >
            {castleLoading ? t("Doğrulanıyor...") : t("Kaydet ve Kilitle")}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: 'rgba(10, 25, 41, 0.8)', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, minWidth: 0 }}>
              <img src="/logo192.png" alt="GGE Bot logosu" style={{ height: 32, width: 32, marginRight: 10, borderRadius: 6 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" noWrap sx={{ fontWeight: 'bold', letterSpacing: 1 }}>GGE Bot</Typography>
                <Typography
                  variant="caption"
                  sx={{ display: { xs: 'none', md: 'block' }, color: 'rgba(255,255,255,0.65)', lineHeight: 1.15 }}
                >
                  Ban riskiniz düşük bile olsa bu olasılık her zaman vardır. Lütfen bu riskleri bilerek kullanın.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flexGrow: 1 }} />

            {/* DİL SEÇİCİ */}
            <IconButton onClick={handleLangMenu} sx={{ mr: 1, color: '#90caf9' }}>
              <LanguageIcon />
              <Typography variant="button" sx={{ ml: 1, fontWeight: 'bold' }}>{language.toUpperCase()}</Typography>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => handleLangSelect('tr')}>Türkçe</MenuItem>
              <MenuItem onClick={() => handleLangSelect('en')}>English</MenuItem>
            </Menu>

            <Avatar sx={{ bgcolor: '#90caf9', color: '#000', width: 32, height: 32 }}>
              {(() => {
                if (adminViewingUser && allAccounts.length > 0) {
                  const viewed = allAccounts.find(a => a.userId === adminViewingUser);
                  return viewed?.ownerName?.[0]?.toUpperCase() || viewed?.username?.[0]?.toUpperCase() || 'A';
                }
                return selectedUser?.name?.[0]?.toUpperCase() || 'U';
              })()}
            </Avatar>
          </Toolbar>
        </AppBar>

        <Drawer variant="permanent" sx={{ width: drawerWidth, flexShrink: 0, [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box', borderRight: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column' } }}>
          <Toolbar />
          <Box sx={{ overflow: 'auto', mt: 2, flexGrow: 1 }}>
            <List sx={{ px: 1 }}>
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton selected={activeView === 'dashboard'} onClick={() => { setActiveView('dashboard'); setSettingsOpen(false); }} sx={{ borderRadius: 2 }}>
                  <ListItemIcon><DashboardIcon color={activeView === 'dashboard' ? 'primary' : 'inherit'} /></ListItemIcon>
                  <ListItemText primary={t("Dashboard")} />
                </ListItemButton>
              </ListItem>

              {/* Bot Ayarları Sadece Bot Varsa Görünür */}
              {selectedUser && (
                <>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => setSettingsOpen(!settingsOpen)} sx={{ borderRadius: 2 }}>
                      <ListItemIcon><SettingsIcon color={activeView === 'settings' ? 'primary' : 'inherit'} /></ListItemIcon>
                      <ListItemText primary={t("Bot Settings")} secondary={selectedUser.name} />
                      {settingsOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>

                  <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 2 }}>
                      <ListItemButton selected={activeView === 'settings' && settingsTab === 'account'} onClick={() => { setActiveView('settings'); setSettingsTab('account'); }} sx={{ borderRadius: '10px 0 0 10px', mt: 0.5 }}>
                        <ListItemIcon><AccountCircleIcon sx={{ fontSize: 20 }} /></ListItemIcon>
                        <ListItemText primary={t("Account Details")} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                      </ListItemButton>

                      <ListItemButton selected={activeView === 'settings' && settingsTab === 'attack'} onClick={() => { setActiveView('settings'); setSettingsTab('attack'); }} sx={{ borderRadius: '10px 0 0 10px', mt: 0.5, bgcolor: 'rgba(144, 202, 249, 0.08)' }}>
                        <ListItemIcon><GpsFixedIcon sx={{ fontSize: 20, color: '#ff9800' }} /></ListItemIcon>
                        <ListItemText primary={t("SALDIRI AYARLARI")} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#ff9800' }} />
                      </ListItemButton>

                      <Divider sx={{ my: 1, mx: 2, opacity: 0.1 }} />

                      {renderPluginGroup("Saldırı Eklentileri", null, null, attackGroupPlugins, attackGroupOpen, setAttackGroupOpen, '/plugin-icons/attack-group.png')}
                      {renderPluginGroup("Üretim / Alet / Asker", <PrecisionManufacturingIcon />, '#90caf9', productionGroupPlugins, productionGroupOpen, setProductionGroupOpen)}
                    </List>
                  </Collapse>
                </>
              )}
            </List>
          </Box>

          <Box sx={{ px: 1, pb: 2 }}>
            <Divider sx={{ mb: 1, opacity: 0.1 }} />
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => window.location.href = "signin.html"}
                sx={{
                  borderRadius: 2,
                  '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                }}
              >
                <ListItemIcon><LogoutIcon sx={{ color: '#ff5252' }} /></ListItemIcon>
                <ListItemText
                  primary={t("Logout")}
                  primaryTypographyProps={{ sx: { color: '#ff5252', fontWeight: 'bold' } }}
                />
              </ListItemButton>
            </ListItem>
            <Box sx={{ mt: 1.5, px: 1, py: 1, borderRadius: 2, bgcolor: 'rgba(144,202,249,0.04)', border: '1px solid rgba(144,202,249,0.08)', textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: '#546e7a', display: 'block', mb: 0.4 }}>
                Sorun veya destek için:
              </Typography>
              <Typography
                component="a"
                href="mailto:yen1ceri@protonmail.com"
                variant="caption"
                sx={{ color: '#90caf9', textDecoration: 'none', fontWeight: 600, fontSize: '0.72rem', '&:hover': { textDecoration: 'underline' } }}
              >
                yen1ceri@protonmail.com
              </Typography>
            </Box>
          </Box>
        </Drawer>


        <Box component="main" sx={{ flexGrow: 1, p: 4, minHeight: '100vh', bgcolor: '#050c1a' }}>
          <Toolbar />
          {announcement && announcement.id !== dismissedAnnouncement && (
            <Alert severity={announcement.type || 'info'} onClose={dismissAnnouncement} sx={{ mb: 3, whiteSpace: 'pre-line', fontSize: '0.95rem' }}>
              {announcement.text}
              {announcement.link && (
                <> — <a href={announcement.link} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', fontWeight: 600 }}>Detaylar</a></>
              )}
            </Alert>
          )}
          {activeView === 'settings' && plugins.find(p => p.key === settingsTab)?.maintenance && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              🛠 <b>Bakımda:</b> {plugins.find(p => p.key === settingsTab).maintenance} Ayarlarınız korunuyor; bakım bitince eklenti önceki durumuyla geri gelir.
            </Alert>
          )}
          {activeView === 'dashboard' ? (
            <GGEUserTable ws={ws} plugins={plugins} rows={users} usersStatus={usersStatus} language={language} onSelectUser={handleBotSelect} isAdmin={isAdmin} allAccounts={allAccounts} adminViewingUser={adminViewingUser} onAdminSelectUser={handleAdminSelectUser} linkedAccounts={linkedAccounts} linkedViewingUser={linkedViewingUser} onLinkedSelectUser={handleLinkedSelectUser} />
          ) : (
            <UserSettings
              ws={ws}
              selectedUser={selectedUser}
              userStatus={usersStatus[selectedUser?.id]}
              plugins={plugins}
              language={language}
              activeTab={settingsTab}
              credentialError={credentialError}
              credentialVerifying={credentialVerifying}
              clearCredentialError={() => setCredentialError('')}
              onPluginStateChange={setPluginState}
            />
          )}
        </Box>
      </Box>
    </ThemeProvider>
  )
}

export default App
